import { AccountReceivableDTO, Workflow, WorkflowsService } from '@/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useStore } from '@nanostores/react';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setNodes: any;
  selectedNode: any;
  workFlowId: string;
}

const fetchAccountReceivables = async (workflow: Workflow) => {
  const pausedAccountReceivables =
    await WorkflowsService.getUserPausedWorkflowExecutions(workflow.id);

  if (!pausedAccountReceivables.accountReceivables) return [];

  return pausedAccountReceivables.accountReceivables;
};

export default function WorkflowResumeDrawer({
  open,
  setOpen,
  setNodes,
  selectedNode,
  workFlowId,
}: Props) {
  const org = useStore($org);
  const workflow = useStore($workflow);
  const [accounts, setAccounts] = useState<AccountReceivableDTO[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);

  useEffect(() => {
    if (workflow) {
      fetchAccountReceivables(workflow).then(setAccounts);
    }
  }, [workflow]);

  const handleSelectAll = () => {
    if (selectedAccountIds.length === accounts.length) {
      setSelectedAccountIds([]); // Deselect all
    } else {
      setSelectedAccountIds(accounts.map(a => a.id)); // Select all
    }
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccountIds(
      prev =>
        prev.includes(accountId)
          ? prev.filter(id => id !== accountId) // Remove if selected
          : [...prev, accountId], // Add if not selected
    );
  };

  const handleRun = async () => {
    if (selectedAccountIds.length === 0) {
      toast.error('Please select at least one account');
      return;
    }

    try {
      toast.success('Resuming workflow execution(s)...');
      setOpen(false); // Close drawer after successful execution

      await WorkflowsService.manualResumeBulkWorkflowExecution(workFlowId, {
        accountReceivableIds: selectedAccountIds,
      });
    } catch (error) {
      toast.error('Failed to resume workflow execution');
      console.error(error);
    }
  };

  return (
    <div
      className={`absolute right-6 top-20 min-h-[160px] min-w-[360px] rounded-lg bg-white p-4 pl-6 pr-6 ${!open && 'hidden'} z-10`}
    >
      <div className="flex justify-between">
        <div className="flex items-center">
          <p className="text-sm font-semibold tracking-tight">
            Workflow Resume Run
          </p>
        </div>
        <button type="button" onClick={() => setOpen(false)}>
          <X className="h-4 w-4" />
        </button>
      </div>

      {accounts.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-3 text-sm font-semibold">Account Receivables:</h3>

          <div className="mb-2 flex items-center">
            <Checkbox
              checked={selectedAccountIds.length === accounts.length}
              onCheckedChange={handleSelectAll}
            />
            <label className="ml-2 text-sm font-medium">
              Select All ({accounts.length})
            </label>
          </div>

          <div className="max-h-[300px] space-y-2 overflow-y-auto">
            {accounts.map(account => (
              <div
                key={account.id}
                className="flex items-center justify-between"
              >
                <div className="flex items-center">
                  <Checkbox
                    checked={selectedAccountIds.includes(account.id)}
                    onCheckedChange={() => handleAccountSelect(account.id)}
                  />
                  <div className="ml-2 text-sm font-medium">
                    {account.customer?.contactEmail}
                  </div>
                </div>
                <Button variant="secondary" size="sm" className="h-6">
                  {account.status}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleRun}
              disabled={selectedAccountIds.length === 0}
            >
              Resume Workflow
            </Button>
          </div>
        </div>
      )}

      {accounts.length === 0 && (
        <div className="mt-4 text-sm text-muted-foreground">
          No accounts found for this workflow to resume.
        </div>
      )}
    </div>
  );
}
